/* eslint-disable @typescript-eslint/no-explicit-any */
// src/app/services/cookie.service.ts

import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { environment } from '~environments/environment';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { SsrRequestService } from './ssr-request.service';
import { SsrResponseService } from './ssr-response.service';

@Injectable()
export class SsrCookieService {
  protected _platformId = inject(PLATFORM_ID);
  protected _cookieService = inject(CookieService);
  protected _ssrRequestService = inject(SsrRequestService);
  protected _ssrResponseService = inject(SsrResponseService);

  set(name: string, value: string, options?: CookieOptions): void {
    const path = options?.path ?? '/';
    const secure = (options?.secure ?? environment.production) ? true : false;
    const domain =
      options?.domain || environment.masterDomainName.includes('localhost')
        ? environment.masterDomainName
        : `.${environment.masterDomainName}`;
    if (this._isPlatformBrowser) {
      this._cookieService.put(name, value, {
        ...options,
        path,
        domain,
        secure
      });
    }
    if (this._isPlatformServer) {
      this._ssrResponseService.response?.cookie(name, value, {
        ...options,
        path,
        secure,
        domain
      } as any);
    }
  }

  get(name: string): string {
    if (this._isPlatformBrowser) {
      return this._cookieService.get(name) || '';
    }

    if (this._isPlatformServer) {
      const cookies = this._parseHeaderCookie(
        this._ssrRequestService.request?.headers.cookie
      );
      return cookies[name] ? JSON.stringify(cookies[name]) : '';
    }
    return '';
  }

  delete(name: string): void {
    if (this._isPlatformBrowser) {
      this._cookieService.remove(name);
    }

    if (this._isPlatformServer) {
      this._ssrResponseService.response?.clearCookie(name);
    }
  }

  getAll(): { [key: string]: string } {
    if (this._isPlatformBrowser) {
      return this._cookieService.getAll();
    }

    if (this._isPlatformServer) {
      return this._parseHeaderCookie(
        this._ssrRequestService.request?.headers.cookie
      );
    }
    return {};
  }

  protected get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  protected get _isPlatformServer(): boolean {
    return isPlatformServer(this._platformId);
  }

  protected _parseHeaderCookie = (
    headerCookie: string | null | undefined
  ): Record<string, string> => {
    if (!headerCookie) return {};

    return headerCookie
      .split(';')
      .map(v => v.split('='))
      .reduce((acc: Record<string, string>, v) => {
        acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());
        return acc;
      }, {});
  };
}
